import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Input, Select, DatePicker, notification, Checkbox, Row, Col, Radio, Dropdown, List, Spin } from 'antd';
import { checkCars, checkHasCar, getOilTypes, oilChangeDetails } from '../../app/api';
import { debounce } from '../../helpers/helpers';

const { TextArea } = Input;

const OilBlank = () => {
    const [form] = Form.useForm();
    const formRef = useRef(null);
    const [allTypes, setAllTypes] = useState([]);
    const [isCarRegistered, setIsCarRegistered] = useState(false);
    const [serviceType, setServiceType] = useState('engine_oil');
    const [carExistsMessage, setCarExistsMessage] = useState('');
    const [carNumberExists, setCarNumberExists] = useState(false);
    const [vinExistsMessage, setVinExistsMessage] = useState('');
    const [vinExists, setVinExists] = useState(false);
    const [userNameExistsMessage, setUserNameExistsMessage] = useState('');
    const [userNameExists, setUserNameExists] = useState(false);
    const [carName, setCarName] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [carResults, setCarResults] = useState([]);
    const [carItemData, setCarItemData] = useState(null);
    const [isHasCar, setIsHasCar] = useState(false);
    const [isBtnDiasabled, setIsBtnDiasabled] = useState(false);

    const onFinish = async (values) => {
        setIsBtnDiasabled(true);
        try {
            const newData = {
                ...values,
                ...(values.carData ? { car_id: values.carData.id } : null),
                createdAt: values.createdAt.format('YYYY-MM-DD HH:mm:ss'),
                ...(values.car_number ? { car_number: values.car_number.toUpperCase() } : {}),
            };
            delete newData.carData;
            delete newData.isCarRegistered;
            if (newData.additionalNotes === undefined) {
                delete newData.additionalNotes;
            }
    
            const response = await oilChangeDetails(newData);
    
            if (response.status === 201) {
                form.resetFields();
                window.location.href = '/oil-done'
            } else if (response.statusCode === 400) {
                notification.error(response.message);
            }
            notification.success({
                message: 'Յուղի փոխարինման ծառայությունը ամրագրված է!',
                description: 'Ձեր յուղի փոխարինման այցը հաջողությամբ ամրագրվել է:',
            });
        } catch (err) {
            console.log("Error", err);
        } finally {
            setIsBtnDiasabled(false);
        }
    };

    const handleSearch = useMemo(() => 
        debounce(async (value) => {
          if (!value) {
            setCarResults([]);
            return;
          }
      
          setIsLoading(true);
          try {
            const response = await checkHasCar(value);
            if (response.data.success) {
              const carData = response.data.data;
              setCarResults(carData.length > 0 ? carData : []);
            }
          } catch (error) {
            console.error("Error during search:", error);
          }
          setIsLoading(false);
        }, 500), 
    []);

    const handleSelectCar = (car) => {
        setCarName(car.car_number);
        setCarResults([]);
        setCarItemData(car);
        if (car) {
            setIsHasCar(true);
        } else {
            setIsHasCar(false);
        }
    };

    const checkCarInfo = async (field, value) => {
        if (value) {
            try {
                let response;
                if (field === "username") {
                    await new Promise((resolve) => {
                        debounce(async (value) => {
                            response = await checkCars(field, value);
                            resolve();
                        }, 800)(value);
                    });
                } else {
                    response = await checkCars(field, value);
                }

                if (!response.data.success) {
                    if (field === 'win_number') {
                        setVinExists(true);
                        setVinExistsMessage('Նման VIN համարը արդեն գոյություն ունի։');
                    }
                    if (field === 'car_number') {
                        setCarNumberExists(true);
                        setCarExistsMessage('Նման մեքենայի համարանիշը արդեն գոյություն ունի։');
                    }
                    if (field === "username") {
                        setUserNameExists(true);
                        setUserNameExistsMessage('Նման օգտանունով արդեն գոյություն ունի։');
                    }
                } else {
                    if (field === 'win_number') {
                        setVinExists(false);
                        setVinExistsMessage('');
                    }
                    if (field === 'car_number') {
                        setCarNumberExists(false);
                        setCarExistsMessage('');
                    }
                    if (field === 'username') {
                        setUserNameExists(false);
                        setUserNameExistsMessage('');
                    }
                }
            } catch (error) {
                console.error('Error checking car info: ', error);
            }
        }
    };

    const carNumberValidator = (value) => {
        const firstPart = /^[0-9]{2}/;
        const secondPart = /^[A-Z]{2}/;
        const thirdPart = /^[0-9]{3}$/;
    
        if (value) {
            const upperCaseValue = value.toUpperCase();
            
            if (!firstPart.test(upperCaseValue.slice(0, 2))) {
                return Promise.reject('Առաջին երկու բնագավառները պետք է լինեն թվեր');
            }
            
            if (!secondPart.test(upperCaseValue.slice(2, 4))) {
                return Promise.reject('Երկու հաջորդ բնագավառները պետք է լինեն մեծատառեր');
            }
            
            if (!thirdPart.test(upperCaseValue.slice(4, 7))) {
                return Promise.reject('Վերջին երեք բնագավառները պետք է լինեն թվեր');
            }
        }
    
        return Promise.resolve();
    };

    const validateVin = (vin) => {
        const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
        if (!vinRegex.test(vin)) {
            return 'Սխալ VIN համարը։ Այն պետք է լինի 17 նշան և պարունակի միայն թույլատրված նշաններ։';
        }
        return 'VIN համարը ճիշտ է։';
    };

    const allOilTypes = async () => {
        const allOilTypes = await getOilTypes();
        const oil_types_data = allOilTypes.data;
        if (allOilTypes.status === 200) {
            setAllTypes(oil_types_data.data);
        }
    };

    useEffect(() => {
        allOilTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        formRef.current?.setFieldsValue({
            carData: carItemData,
        });
    }, [carItemData]);

    useEffect(() => {
        if (serviceType !== "engine_oil") {
            formRef.current?.resetFields(['oil_type']);
        }
    }, [serviceType]);

    return (
        <div style={styles.container}>
            <div style={styles.formContainer}>
                <h1 style={styles.header}>Մեքենայի յուղի փոխման թերթիկ</h1>
                <p style={styles.description}>Լրացրեք ստորև ներկայացված տվյալները՝ յուղափոխման այցը ամրագրելու համար</p>

                <Form onFinish={onFinish} ref={formRef} layout="vertical" style={styles.form} initialValues={{ runUnit: 'km', carData: {} }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Սպասարկման տեսակ"
                                name="serviceType"
                                initialValue="engine_oil"
                            >
                                <Select
                                    placeholder="Ընտրեք սպասարկման տեսակը"
                                    value={serviceType}
                                    onChange={(value) => {
                                        setServiceType(value);
                                    }}
                                >
                                    <Select.Option value="engine_oil">Շարժիչի յուղի փոխարինում</Select.Option>
                                    <Select.Option value="transmission">Փոխանցման տուփի փոխարինում</Select.Option>
                                    <Select.Option value="hydraulic_or_differential">Հիդրավլիկ կամ մոստ</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label="Մեքենայի համարանիշ"
                                name="carData"
                                rules={[
                                    {
                                        required: !isCarRegistered,
                                        message: 'Մեքենայի համարանիշը պարտադիր է',
                                    },
                                ]}
                            >
                                <Dropdown
                                    overlay={
                                        carResults.length > 0 ? (
                                            <List
                                                bordered
                                                dataSource={carResults}
                                                renderItem={(item) => (
                                                    <List.Item
                                                        onClick={() => handleSelectCar(item)}
                                                        style={{ background: "#fff", cursor: "pointer" }}
                                                    >
                                                        {item.username} 
                                                        <span style={{ color: "red" }}> {item.car_number} </span> 
                                                    </List.Item>
                                                )}
                                                style={{ width: 260, maxHeight: 200, overflowY: "auto", background: "#808080" }}
                                            />
                                        ) : isLoading ? (
                                            <Spin />
                                        ) : (
                                            carName && carResults.length === 0 && !isLoading && (
                                                <div style={{ padding: 10 }}>Տվյալներ չեն գտնվել</div>
                                            )
                                        )
                                    }
                                    open={carResults.length > 0 || isLoading}
                                >
                                    <Input
                                        placeholder="Մեքենայի համարանիշ"
                                        disabled={isLoading}
                                        value={carName}
                                        onChange={(e) => {
                                            setCarName(e.target.value);
                                            handleSearch(e.target.value);
                                            if (e.target.value === "") {
                                                setIsHasCar(false)
                                            }
                                        }}
                                    />
                                </Dropdown>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item 
                                label="Յուղի անվանում"
                                name="oil_name"
                                rules={[{ required: true, message: 'Խնդրում ենք մուտքագրել յուղի անվանումը!' }]}
                            >
                                <Input placeholder="Մուտքագրեք յուղի անվանումը" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label="Յուղի տեսակը"
                                name="oil_type"
                                rules={[{ required: true, message: 'Խնդրում ենք ընտրել յուղի տեսակը' }]}
                            >
                                {serviceType === "engine_oil" ? <Select
                                    showSearch
                                    placeholder="Ընտրել յուղի տեսակը"
                                    optionFilterProp="children"
                                    onChange={(value) => {
                                        // setSelectedMake(value);
                                        form.setFieldsValue({ model: undefined });
                                    }}
                                    filterOption={(input, option) =>
                                        option?.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {allTypes.map((item) => (
                                        <Select.Option key={item.id} value={item.oil}>
                                            {item.oil}
                                        </Select.Option>
                                    ))}
                                </Select> : <Input placeholder="Խնդրում ենք ընտրել յուղի տեսակը" /> }
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item 
                                label="Վազքը (կմ / մղոն)" 
                                name="run" 
                                rules={[
                                    { required: true, message: 'Խնդրում ենք մուտքագրել վազքը!' },
                                    { pattern: /^[0-9]+$/, message: 'Մուտքագրեք միայն թվեր!' }
                                ]}
                            >
                                <Input type="number" placeholder="Մուտքագրեք վազքը (կմ / մղոն)" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item 
                                label="Հաջորդ փոխման վազքը" 
                                name="nextRun" 
                                rules={[
                                    { required: true, message: 'Խնդրում ենք մուտքագրել հաջորդ փոխման վազքը!' },
                                    { pattern: /^[0-9]+$/, message: 'Մուտքագրեք միայն թվեր!' },
                                ]}
                            >
                                <Input 
                                    type="number" 
                                    placeholder="Մուտքագրեք հաջորդ փոխման վազքը (կմ / մղոն)"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                        <Form.Item 
                            label="Ամսաթիվ"
                            name="createdAt" 
                            rules={[{ required: true, message: 'Խնդրում ենք ընտրել սպասարկման նախընտրելի ամսաթիվը!' }]}
                        >
                            <DatePicker style={{ width: '100%' }} placeholder='Ընտրել ամսաթիվը' />
                        </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Վազքի միավոր" name="runUnit">
                                <Radio.Group>
                                    <Radio value="km">Կմ</Radio>
                                    <Radio value="mile">Մղոն</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="Ֆիլտրեր">
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="oilFilter"
                                    valuePropName="checked"
                                    initialValue={false}
                                    noStyle
                                >
                                    <Checkbox>Յուղի ֆիլտր</Checkbox>
                                </Form.Item>
                            </Col>
                            { serviceType === "engine_oil" ? 
                                <>
                                    <Col span={8}>
                                        <Form.Item
                                            name="cabinFilter"
                                            valuePropName="checked"
                                            initialValue={false}
                                            noStyle
                                        >
                                            <Checkbox>Սրահի ֆիլտր</Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            name="airFilter"
                                            valuePropName="checked"
                                            initialValue={false}
                                            noStyle
                                        >
                                            <Checkbox>Օդի ֆիլտր</Checkbox>
                                        </Form.Item>
                                    </Col>
                                 </> : null }
                        </Row>
                    </Form.Item>

                    <Form.Item
                        name="additionalNotes"
                        label="Հավելյալ նշումներ"
                    >
                        <TextArea 
                            autoSize={{ minRows: 3, maxRows: 5 }} 
                            placeholder="Մուտքագրեք ավելին..." 
                        />
                    </Form.Item>

                    {!isHasCar ? <Form.Item name="isCarRegistered" valuePropName="checked">
                        <Checkbox 
                            onChange={(e) => {
                                setIsCarRegistered(e.target.checked);
                                if (!e.target.checked) {
                                    form.setFieldsValue({
                                        username: '', 
                                        car_number: '',  
                                        win_number: '',   
                                    });
                                } else {
                                    setCarName("");
                                    setCarResults([])
                                }
                            }}
                        >
                            Մեքենան գրանցվա՞ծ է պահեստում
                        </Checkbox>
                    </Form.Item> : null}

                    {isCarRegistered && <div
                        style={{
                            marginBottom: '20px',
                            padding: '15px',
                            border: '1px solid #d9d9d9',
                            borderRadius: '8px',
                            backgroundColor: '#fafafa',
                        }}
                        >
                        <h3 style={{ marginBottom: '15px' }}>Հաճախորդի Տվյալներ</h3>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Օգտատերի Անուն"
                                        name="username"
                                        rules={[
                                            {
                                                validator: async (_, value) => {
                                                    checkCarInfo('username', value);
                                                },
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Օրինակ՝ Աշոտ" onChange={(e) => setUserNameExistsMessage("")} />
                                    </Form.Item>
                                    {userNameExists && <span style={{ color: 'red' }}>{userNameExistsMessage}</span>}
                                </Col>

                                <Col span={12}>
                                    <Form.Item
                                        label="Մեքենայի համարանիշ"
                                        name="car_number"
                                        rules={[
                                            {
                                                validator: async (_, value) => {
                                                    await carNumberValidator(value);
                                                    checkCarInfo('car_number', value);
                                                },
                                            },
                                        ]}
                                    >
                                        <Input 
                                            placeholder="Օրինակ՝ 13AB666"
                                            style={{ textTransform: 'uppercase' }}
                                            maxLength={7}
                                            onChange={(e) => {
                                                setCarExistsMessage("");
                                                let value = e.target.value.toUpperCase();
                                                if (/[^A-Z0-9]/.test(value)) {
                                                    value = value.replace(/[^A-Z0-9]/g, '');
                                                }

                                                if (value.length <= 8) {
                                                    e.target.value = value;
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                    {carNumberExists && <span style={{ color: 'red' }}>{carExistsMessage}</span>}
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="VIN համար"
                                        name="win_number"
                                        rules={[
                                            {
                                                validator: async (_, value) => {
                                                    await validateVin(value);
                                                    checkCarInfo('win_number', value);
                                                },
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Օրինակ՝ JM1GJ1U62E1423456" onChange={(e) => setVinExistsMessage("")} />
                                    </Form.Item>
                                    {vinExists && <span style={{ color: 'red' }}>{vinExistsMessage}</span>}
                                </Col>
                            </Row>
                    </div>}

                    <Form.Item>
                        <Button type="primary" htmlType="submit" style={styles.submitButton} disabled={isBtnDiasabled}>
                           Հաստատել
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    formContainer: {
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '600px',
    },
    header: {
        textAlign: 'center',
        color: '#333',
        marginBottom: '10px',
    },
    description: {
        textAlign: 'center',
        color: '#666',
        marginBottom: '20px',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
    },
    submitButton: {
        width: '100%',
    },
};

export default OilBlank;
